// Global Instructions Rule Applied!
//
// Funds section of the statement run form (spec A3.14 stage 5).
//
// A run holds one fund or many; funds join as their books arrive. Per fund
// the section shows where its statements stand and offers Generate (or
// Generate again, which replaces the stored statements) and Remove. "Add
// funds" lists what can join: a legal entity of a consolidated fund cannot -
// its statements are made with the consolidated fund - and neither can a
// fund already in another run for the period.
//
// The gate. A fund whose statements cannot be made is not generated: its row
// reads Blocked and lists why (the books do not reconcile, no legal name, no
// notice sender, ...). The two things a user fixes on the fund's Terms tab -
// legal name and notice sender - show on the row as soon as the fund is
// added, before anything is generated.
//
// Review (stage 5 part 3). Once a fund's statements have been looked through
// they are "marked as issued": the record that they were given to the
// investors. From then on a statement generated again is a new version,
// printed "Revised". An application administrator can undo the mark, and
// alone removes a fund that was issued. A fund whose stored statements no
// longer match the books or the settings says so ("N out of date"): that is
// asked per fund after the form has loaded, and reads "Checking" until then.
// "Download all" hands back the fund's stored PDFs as one archive.
//
// Review workbook (owner, 2026-10-27): the reviewer's file per legal fund
// as of the run's period end - the statements, the schedule of
// investments, the partners' capital, the trial balance and the journal
// lines as linked tabs - from the row (a consolidated fund: one per legal
// entity, zipped) and for the whole run from the header. Built live, so it
// reads the books as they are now, not as the stored statements were made.
//
// The generation runs on the server in the background, one fund after
// another, so the run is re-read while anything is queued and the section
// shows the progress (the capital call form's Notices section is the
// pattern).
//
// Document groups (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1; owner
// 2026-10-29): a fund whose statement settings include the fund report
// produces two kinds of document - its fund report (the `reports` group) and
// its statements (`statements`) - each generated and marked as issued on its
// own, so the reports can go to the investors a week before the statements.
// Such a fund's Status column adds a line per group, and its Generate and
// Mark as issued buttons are split buttons (the ribbon grammar): the main
// button acts on every group, the menu on one. A fund that produces
// statements alone keeps the plain buttons. A split button cannot sit inside
// a Popconfirm (the arrow would open it too), so its confirmations go
// through Modal.useModal (the static Modal.confirm renders nothing under
// React 19).
//
// The investor performance decks (d.1, PR 4) are run-level documents: a
// whole-run generation that includes the fund reports (Generate all,
// Generate all fund reports) makes one per investor account of the run
// after the funds' documents, the progress line says so while they print,
// and Mark reports as issued on any fund issues them with the reports. They
// are reviewed in their own section below the statements.

import React, { useCallback, useMemo, useState } from 'react'
import { Alert, Button, Dropdown, Modal, Popconfirm, Progress, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, DownOutlined, FileExcelOutlined, PlusOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { TABLE_FUNDS, TABLE_FUND_GROUPS } from '../../../state_manager/globals.ts'
import {
  addRunFunds,
  downloadRunReviewWorkbooks,
  fetchRunFundOptions,
  issueRunFund,
  removeRunFund,
  unissueRunFund
} from '../services/investorStatementsService.ts'
import {
  DocumentGroup,
  InvestorStatementRun,
  InvestorStatementRunFund,
  InvestorStatementRunFundGroup,
  InvestorStatementRunFundOption,
  RunFundState
} from '../../../state_manager/data_models/investor_statement_run.ts'
import {
  checkLabel,
  enabledGroups,
  fileSizeText,
  groupStateText,
  isGroupIssuable,
  isGroupIssued,
  isIssued,
  issuedText,
  RUN_TABLE_SCROLL,
  runFundStateTag
} from '../utils/runDisplay.ts'
import { downloadStatements } from '../utils/storedStatements.ts'
import type { RunFundChangesMap } from '../hooks/useRunFundChanges.ts'

const { Text } = Typography

const RECORD_LINK_CLASS =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'

type GenerateTarget = { runFundIds?: string[] | null; statementIds?: string[] | null; documentGroupCodes?: string[] | null }

/** One document group to act on: its code and what the sentences call it
 *  ('fund reports'); null = every group ('documents' when the fund has more
 *  than one, 'statements' otherwise). */
type GroupChoice = { codes: string[]; words: string } | null

const groupChoice = (group: InvestorStatementRunFundGroup | DocumentGroup): GroupChoice => ({
  codes: [group.statement_document_groups__code],
  words: group.statement_document_groups__label.toLowerCase()
})

interface RunFundsSectionProps {
  run: InvestorStatementRun
  /** Queue a generation; the hook re-reads the run. */
  generate: (target?: GenerateTarget) => Promise<{ count: number | null; error?: string }>
  generating: boolean
  /** A write that returned the run hands it in. */
  onRunChange: (run: InvestorStatementRun) => void
  refetch: () => Promise<void>
  /** "Out of date", per fund of the run (useRunFundChanges). */
  changes: RunFundChangesMap
}

const REVISED_NOTE =
  'These statements were issued: each one generated again is a new version, printed "Revised" with the day it is generated. The issued files are kept.'
const REPLACE_NOTE =
  'Each stored statement is replaced by a new one made from the books as they are now. The earlier files are kept as superseded.'

const isBusy = (fund: InvestorStatementRunFund) =>
  fund.prog__state === RunFundState.queued || fund.prog__state === RunFundState.generating

const kindLabel = (kind: 'fund' | 'fund_group') => (kind === 'fund_group' ? 'Consolidated fund' : 'Fund')

/** The fund produces more than one kind of document (its fund report and
 *  its statements): the per-group actions and states apply. */
const hasGroups = (fund: InvestorStatementRunFund) => enabledGroups(fund).length > 1

/** What a fund-wide sentence calls the fund's documents. */
const fundWords = (fund: InvestorStatementRunFund | null) => (fund && hasGroups(fund) ? 'documents' : 'statements')

/** 'Fund reports: Issued 10/29/2026 · Statements: Generated 12 of 14'. */
function groupStatesLine(fund: InvestorStatementRunFund): React.ReactNode {
  if (!hasGroups(fund)) return null
  return (
    <div className='text-xs text-gray-500 mt-1'>
      {enabledGroups(fund).map((group, i) => (
        <span key={group.statement_document_groups__code}>
          {i > 0 ? ' · ' : ''}
          {group.statement_document_groups__label}: {groupStateText(group)}
        </span>
      ))}
    </div>
  )
}

/** "Out of date" on a fund's row: Checking until the fund has answered. */
function changeTags(change: RunFundChangesMap[string] | undefined): React.ReactNode {
  if (!change) return null
  if (change.status === 'checking') {
    return (
      <Tooltip title='Comparing the stored statements with the books and the settings as they are now.'>
        <Tag>Checking</Tag>
      </Tooltip>
    )
  }
  if (change.status !== 'done') {
    return (
      <Tooltip title={change.message}>
        <Tag color='gold'>Cannot be compared</Tag>
      </Tooltip>
    )
  }
  return (
    <>
      {change.staleCount > 0 ? (
        <Tooltip title='The books or the settings have changed since these statements were generated. Generate them again to replace them.'>
          <Tag color='gold'>{change.staleCount} out of date</Tag>
        </Tooltip>
      ) : null}
      {change.missingCount > 0 ? (
        <Tooltip title='Investors on the statements of this period who have no stored statement. Generate the fund again.'>
          <Tag color='gold'>
            {change.missingCount} without a statement
          </Tag>
        </Tooltip>
      ) : null}
    </>
  )
}

interface AddState {
  options: InvestorStatementRunFundOption[] | null
  loading: boolean
  error: string | null
  selected: string[]
}

export default function RunFundsSection({
  run,
  generate,
  generating,
  onRunChange,
  refetch,
  changes
}: RunFundsSectionProps) {
  const { openFormInTab } = useTabManagerContext()
  // the confirmations of the split buttons (a Popconfirm cannot wrap them)
  const [modal, modalContext] = Modal.useModal()
  const runId = run.run.investor_statement_runs__id
  const funds = run.funds
  const documentGroups = run.run.prog__document_groups
  // some fund of the run produces its fund report too: the header offers
  // Generate all per group
  const runHasGroups = funds.some(hasGroups)
  const [busyFund, setBusyFund] = useState<string | null>(null)
  const [add, setAdd] = useState<AddState | null>(null)
  const [adding, setAdding] = useState(false)
  // the fund whose issue is being marked or undone / whose files are fetched
  const [issuingFund, setIssuingFund] = useState<string | null>(null)
  const [downloadingFund, setDownloadingFund] = useState<string | null>(null)
  // the run fund whose review workbooks are being built ('' = the whole run)
  const [reviewingFund, setReviewingFund] = useState<string | null>(null)
  const isAdmin = run.run.prog__is_app_admin

  const handleReviewWorkbooks = useCallback(
    async (fund: InvestorStatementRunFund | null) => {
      setReviewingFund(fund ? fund.investor_statement_run_funds__id : '')
      try {
        const res = await downloadRunReviewWorkbooks(
          runId,
          fund ? fund.investor_statement_run_funds__id : null,
          getAccessToken(store.getState().auth)
        )
        if (!res.success) showErrorToast(res.error || 'Failed to download the review workbooks')
      } finally {
        setReviewingFund(null)
      }
    },
    [runId]
  )

  // the performance decks of the run, still being generated after the funds
  const decks = run.prog__account_document_summary
  const decksBusy = decks.pending > 0
  const deckType = run.run.prog__account_document_type
  const anyBusy = funds.some(isBusy) || decksBusy
  const generatedBefore = funds.some((f) => f.prog__counts.total > 0)

  const openEntity = useCallback(
    (fund: InvestorStatementRunFund) => {
      const id = fund.prog__kind === 'fund_group' ? fund.fund_groups__id : fund.funds__id
      if (id) openFormInTab(fund.prog__kind === 'fund_group' ? TABLE_FUND_GROUPS : TABLE_FUNDS, id, fund.prog__name)
    },
    [openFormInTab]
  )

  const runGenerate = useCallback(
    async (fund: InvestorStatementRunFund | null, group: GroupChoice = null) => {
      if (fund) setBusyFund(fund.investor_statement_run_funds__id)
      const words = group?.words ?? (fund ? fundWords(fund) : runHasGroups ? 'documents' : 'statements')
      try {
        const res = await generate({
          ...(fund ? { runFundIds: [fund.investor_statement_run_funds__id] } : {}),
          ...(group ? { documentGroupCodes: group.codes } : {})
        })
        if (res.error || res.count == null) {
          showErrorToast(res.error || `Failed to generate the ${words}`)
          return
        }
        toast.success(
          fund
            ? `Generating the ${words} of ${fund.prog__name}`
            : `Generating the ${words} of ${res.count} fund${res.count === 1 ? '' : 's'}`
        )
      } finally {
        if (fund) setBusyFund(null)
      }
    },
    [generate, runHasGroups]
  )

  /** Generate (again) through a confirmation when stored documents would be
   *  replaced - the split buttons' path (Modal.useModal). */
  const confirmGenerate = useCallback(
    (fund: InvestorStatementRunFund | null, group: GroupChoice, replaces: boolean, issuedAny: boolean) => {
      if (!replaces) {
        void runGenerate(fund, group)
        return
      }
      const words = group?.words ?? fundWords(fund)
      // a whole-run generation that includes the fund reports also makes the
      // performance deck of every investor account of the run
      const withDecks =
        fund === null && deckType !== null && deckType !== undefined &&
        (group === null || group.codes.includes(deckType.statement_document_groups__code ?? ''))
      modal.confirm({
        title: fund ? `Generate the ${words} of ${fund.prog__name} again?` : `Generate every ${words.replace(/s$/, '')} of the run again?`,
        content: (
          <div className='max-w-sm'>
            {issuedAny ? REVISED_NOTE : REPLACE_NOTE}
            {withDecks ? (
              <div className='mt-1'>
                The {deckType.investor_statement_types__short_label.toLowerCase()} of every investor of the run is
                generated again too, after the funds&apos; documents.
              </div>
            ) : null}
          </div>
        ),
        okText: 'Generate again',
        onOk: () => runGenerate(fund, group)
      })
    },
    [modal, runGenerate, deckType]
  )

  const handleRemove = useCallback(
    async (fund: InvestorStatementRunFund) => {
      setBusyFund(fund.investor_statement_run_funds__id)
      try {
        const res = await removeRunFund(fund.investor_statement_run_funds__id, getAccessToken(store.getState().auth))
        if (!res.success) {
          showErrorToast(res.error || `Failed to remove ${fund.prog__name}`)
          return
        }
        if (res.filesNotRemoved > 0) {
          showErrorToast(
            `${fund.prog__name} was removed, but ${res.filesNotRemoved} of its files could not be moved to the trash.`
          )
        } else {
          toast.success(`${fund.prog__name} removed from the run`)
        }
        await refetch()
      } finally {
        setBusyFund(null)
      }
    },
    [refetch]
  )

  const handleIssue = useCallback(
    async (fund: InvestorStatementRunFund, undo: boolean, group: GroupChoice = null) => {
      const id = fund.investor_statement_run_funds__id
      const words = group?.words ?? fundWords(fund)
      setIssuingFund(id)
      try {
        const token = getAccessToken(store.getState().auth)
        const action = undo ? unissueRunFund : issueRunFund
        // every group (the call as before), or the one group named
        const res = await (group ? action(id, token, group.codes) : action(id, token))
        if (res.error || !res.data) {
          showErrorToast(
            res.error || (undo ? 'Failed to undo the issue' : `Failed to mark the ${words} of ${fund.prog__name} as issued`)
          )
          return
        }
        onRunChange(res.data)
        toast.success(
          undo
            ? `The ${words} of ${fund.prog__name} are no longer marked as issued`
            : `The ${words} of ${fund.prog__name} are marked as issued`
        )
      } finally {
        setIssuingFund(null)
      }
    },
    [onRunChange]
  )

  /** Mark as issued, or undo it, through a confirmation - the split buttons'
   *  path (Modal.useModal). */
  const confirmIssue = useCallback(
    (fund: InvestorStatementRunFund, undo: boolean, group: GroupChoice, total: number) => {
      const words = group?.words ?? fundWords(fund)
      const change = changes[fund.investor_statement_run_funds__id]
      modal.confirm({
        title: undo ? `Undo the issue of the ${words} of ${fund.prog__name}?` : `Mark the ${words} of ${fund.prog__name} as issued?`,
        content: (
          <div className='max-w-sm'>
            {undo ? (
              <>They read as not issued again, and generating them again keeps their version.</>
            ) : (
              <>
                Records that {total === 1 ? 'this document was' : `these ${total} documents were`} given to the investors.
                From then on a document generated again is a new version, printed &quot;Revised&quot;.
                {change?.status === 'done' && change.staleCount > 0 ? (
                  <div className='mt-1 text-amber-700'>
                    {change.staleCount} of the fund&apos;s documents {change.staleCount === 1 ? 'is' : 'are'} out of
                    date: the books, the settings or the Period Highlights have changed since.
                  </div>
                ) : null}
                {change?.status === 'checking' ? (
                  <div className='mt-1 text-amber-700'>They are still being compared with the books.</div>
                ) : null}
              </>
            )}
          </div>
        ),
        okText: undo ? 'Undo issued' : 'Mark as issued',
        onOk: () => handleIssue(fund, undo, group)
      })
    },
    [modal, handleIssue, changes]
  )

  const handleDownloadAll = useCallback(
    async (fund: InvestorStatementRunFund) => {
      const id = fund.investor_statement_run_funds__id
      setDownloadingFund(id)
      try {
        const res = await downloadStatements(
          run.statements.filter((s) => s.investor_statement_run_funds__id === id),
          `Statements - ${fund.prog__name} - ${run.run.prog__period_label}`
        )
        if (res.saved === 0) showErrorToast('None of the statements could be downloaded')
        else if (res.failed.length > 0) showErrorToast(`Not in the download: ${res.failed.join(', ')}`)
      } finally {
        setDownloadingFund(null)
      }
    },
    [run.statements, run.run.prog__period_label]
  )

  const openAdd = useCallback(async () => {
    setAdd({ options: null, loading: true, error: null, selected: [] })
    const res = await fetchRunFundOptions(runId, getAccessToken(store.getState().auth))
    setAdd((current) =>
      current === null
        ? null
        : {
            ...current,
            options: res.data?.options ?? null,
            loading: false,
            error: res.data ? null : res.error || 'Failed to read the funds that can join the run'
          }
    )
  }, [runId])

  const handleAdd = useCallback(async () => {
    if (!add?.options || add.selected.length === 0) return
    const chosen = add.options.filter((o) => add.selected.includes(o.value))
    setAdding(true)
    try {
      const res = await addRunFunds(
        runId,
        chosen.filter((o) => o.prog__kind === 'fund').map((o) => o.value),
        chosen.filter((o) => o.prog__kind === 'fund_group').map((o) => o.value),
        getAccessToken(store.getState().auth)
      )
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to add the funds to the run')
        return
      }
      onRunChange(res.data)
      toast.success(`${chosen.length} fund${chosen.length === 1 ? '' : 's'} added to the run`)
      setAdd(null)
    } finally {
      setAdding(false)
    }
  }, [add, runId, onRunChange])

  const columns = useMemo(
    (): ColumnsType<InvestorStatementRunFund> => [
      {
        title: 'Fund',
        key: 'fund',
        width: 110,
        render: (_: unknown, fund) => (
          <div>
            <button type='button' className={RECORD_LINK_CLASS} onClick={() => openEntity(fund)}>
              {fund.prog__name}
            </button>
            {fund.prog__kind === 'fund_group' ? <Text type='secondary'> · Consolidated fund</Text> : null}
          </div>
        )
      },
      {
        title: 'Status',
        key: 'status',
        render: (_: unknown, fund) => {
          const tag = runFundStateTag(fund)
          const blocked = fund.prog__state === RunFundState.blocked
          // what the gate said when it refused the fund; else what is still
          // missing on the fund itself
          const reasons = blocked ? fund.prog__blocking : []
          const needs = blocked || isBusy(fund) ? [] : fund.prog__problems
          return (
            <div>
              <Space size={4} wrap>
                <Tooltip title={tag.tooltip}>
                  <Tag color={tag.color}>{tag.text}</Tag>
                </Tooltip>
                {needs.map((problem, i) => (
                  <Tooltip key={`${problem.code}-${problem.funds__id ?? i}`} title={problem.message}>
                    <Tag color='gold'>{checkLabel(problem.code)}</Tag>
                  </Tooltip>
                ))}
                {!blocked
                  ? fund.prog__warnings.map((warning, i) => (
                      <Tooltip key={`${warning.code}-${warning.funds__id ?? i}`} title={warning.message}>
                        <Tag color='gold'>{checkLabel(warning.code)}</Tag>
                      </Tooltip>
                    ))
                  : null}
                {isBusy(fund) ? null : changeTags(changes[fund.investor_statement_run_funds__id])}
              </Space>
              {reasons.map((reason, i) => (
                <div key={`${reason.code}-${reason.funds__id ?? ''}-${i}`} className='text-xs text-red-600 mt-1'>
                  {reason.message}
                </div>
              ))}
              {fund.prog__state === RunFundState.failed && fund.investor_statement_run_funds__error_message ? (
                <div className='text-xs text-red-600 mt-1'>{fund.investor_statement_run_funds__error_message}</div>
              ) : null}
              {groupStatesLine(fund)}
            </div>
          )
        }
      },
      {
        title: 'Documents',
        key: 'statements',
        width: 100,
        align: 'right',
        render: (_: unknown, fund) =>
          fund.prog__counts.total > 0 ? `${fund.prog__counts.generated} of ${fund.prog__counts.total}` : '—'
      },
      {
        title: 'Size',
        key: 'size',
        width: 90,
        align: 'right',
        render: (_: unknown, fund) => fileSizeText(fund.prog__file_size_bytes)
      },
      { title: 'Issued', key: 'issued', width: 110, render: (_: unknown, fund) => issuedText(fund) },
      {
        title: '',
        key: 'actions',
        width: 470,
        align: 'right',
        render: (_: unknown, fund) => {
          const id = fund.investor_statement_run_funds__id
          const busy = busyFund === id
          const stored = fund.prog__counts.total > 0
          const counts = fund.prog__counts
          const issuedAny = fund.prog__issued_count > 0
          const change = changes[id]
          // every statement of the fund, or none: an investor must not be
          // left without one while the fund reads Issued
          // (the server's rule: stmt__run_fund_issue)
          const issuable = counts.total > 0 && counts.generated === counts.total
          const removeLocked = issuedAny && !isAdmin
          const groups = enabledGroups(fund)
          const split = hasGroups(fund)
          const generateButton = (
            <Button
              size='small'
              loading={busy && generating}
              disabled={isBusy(fund) || (generating && !busy)}
              onClick={stored ? undefined : () => void runGenerate(fund)}
            >
              {stored ? 'Generate again' : 'Generate'}
            </Button>
          )
          // the groups with something to issue: those with documents, not
          // every one of them issued yet
          const issuableGroups = groups.filter((g) => g.prog__counts.total > 0 && !isGroupIssued(g))
          const undoableGroups = groups.filter((g) => g.prog__issued_count > 0)
          return (
            <Space size={4}>
              {split ? (
                // the fund report and the statements: the main button acts
                // on both, the menu on one
                <Dropdown.Button
                  size='small'
                  loading={busy && generating}
                  disabled={isBusy(fund) || (generating && !busy)}
                  icon={<DownOutlined />}
                  onClick={() => confirmGenerate(fund, null, stored, issuedAny)}
                  menu={{
                    items: groups.map((group) => ({
                      key: group.statement_document_groups__code,
                      label: `Generate ${group.statement_document_groups__label.toLowerCase()}${group.prog__counts.total > 0 ? ' again' : ''}`,
                      onClick: () =>
                        confirmGenerate(fund, groupChoice(group), group.prog__counts.total > 0, group.prog__issued_count > 0)
                    }))
                  }}
                >
                  {stored ? 'Generate again' : 'Generate'}
                </Dropdown.Button>
              ) : stored ? (
                <Popconfirm
                  title={`Generate the statements of ${fund.prog__name} again?`}
                  description={<div className='max-w-sm'>{issuedAny ? REVISED_NOTE : REPLACE_NOTE}</div>}
                  okText='Generate again'
                  disabled={isBusy(fund) || generating}
                  onConfirm={() => runGenerate(fund)}
                >
                  {generateButton}
                </Popconfirm>
              ) : (
                generateButton
              )}
              {split && issuableGroups.length > 0 ? (
                // Mark as issued per group; the main button stamps every
                // group at once (owner Q14)
                <Tooltip
                  title={
                    issuable
                      ? undefined
                      : 'Every document of the fund has to be generated before it is marked as issued whole; mark one kind from the menu.'
                  }
                >
                  <span>
                    <Dropdown.Button
                      size='small'
                      loading={issuingFund === id}
                      disabled={isBusy(fund)}
                      icon={<DownOutlined />}
                      buttonsRender={([left, right]) => [
                        React.cloneElement(left as React.ReactElement<{ disabled?: boolean }>, {
                          disabled: !issuable || isBusy(fund)
                        }),
                        right
                      ]}
                      onClick={() => confirmIssue(fund, false, null, counts.total)}
                      menu={{
                        items: issuableGroups.map((group) => ({
                          key: group.statement_document_groups__code,
                          label: `Mark ${group.statement_document_groups__label.toLowerCase()} as issued`,
                          disabled: !isGroupIssuable(group),
                          onClick: () => confirmIssue(fund, false, groupChoice(group), group.prog__counts.total)
                        }))
                      }}
                    >
                      Mark as issued
                    </Dropdown.Button>
                  </span>
                </Tooltip>
              ) : null}
              {!split && stored && !isIssued(fund) ? (
                <Popconfirm
                  title={`Mark the statements of ${fund.prog__name} as issued?`}
                  description={
                    <div className='max-w-sm'>
                      Records that its {counts.total} statement{counts.total === 1 ? '' : 's'} were given to the
                      investors. From then on a statement generated again is a new version, printed
                      &quot;Revised&quot;.
                      {change?.status === 'done' && change.staleCount > 0 ? (
                        <div className='mt-1 text-amber-700'>
                          {change.staleCount} of them {change.staleCount === 1 ? 'is' : 'are'} out of date: the books or
                          the settings have changed since.
                        </div>
                      ) : null}
                      {change?.status === 'checking' ? (
                        <div className='mt-1 text-amber-700'>They are still being compared with the books.</div>
                      ) : null}
                    </div>
                  }
                  okText='Mark as issued'
                  disabled={!issuable || isBusy(fund)}
                  onConfirm={() => handleIssue(fund, false)}
                >
                  <Tooltip
                    title={issuable ? undefined : 'Every statement of the fund has to be generated before it is marked as issued.'}
                  >
                    <span>
                      <Button size='small' loading={issuingFund === id} disabled={!issuable || isBusy(fund)}>
                        Mark as issued
                      </Button>
                    </span>
                  </Tooltip>
                </Popconfirm>
              ) : null}
              {split && isAdmin && undoableGroups.length > 0 ? (
                <Dropdown.Button
                  size='small'
                  loading={issuingFund === id}
                  disabled={isBusy(fund)}
                  icon={<DownOutlined />}
                  buttonsRender={([left, right]) => [
                    React.cloneElement(left as React.ReactElement<{ disabled?: boolean }>, {
                      disabled: fund.prog__revised_unissued_count > 0 || isBusy(fund)
                    }),
                    right
                  ]}
                  onClick={() => confirmIssue(fund, true, null, counts.total)}
                  menu={{
                    items: undoableGroups.map((group) => ({
                      key: group.statement_document_groups__code,
                      label: `Undo ${group.statement_document_groups__label.toLowerCase()} issued`,
                      disabled: group.prog__revised_unissued_count > 0,
                      onClick: () => confirmIssue(fund, true, groupChoice(group), group.prog__counts.total)
                    }))
                  }}
                >
                  Undo issued
                </Dropdown.Button>
              ) : null}
              {!split && isAdmin && issuedAny ? (
                <Popconfirm
                  title={`Undo the issue of ${fund.prog__name}?`}
                  description={
                    <div className='max-w-sm'>
                      Its statements read as not issued again, and generating them again keeps their version.
                    </div>
                  }
                  okText='Undo issued'
                  disabled={fund.prog__revised_unissued_count > 0 || isBusy(fund)}
                  onConfirm={() => handleIssue(fund, true)}
                >
                  <Tooltip
                    title={
                      fund.prog__revised_unissued_count > 0
                        ? 'A Revised statement has been generated since the issue: it can no longer be undone.'
                        : undefined
                    }
                  >
                    <span>
                      <Button
                        size='small'
                        loading={issuingFund === id}
                        disabled={fund.prog__revised_unissued_count > 0 || isBusy(fund)}
                      >
                        Undo issued
                      </Button>
                    </span>
                  </Tooltip>
                </Popconfirm>
              ) : null}
              {counts.generated > 0 ? (
                <Button
                  size='small'
                  icon={<DownloadOutlined />}
                  loading={downloadingFund === id}
                  onClick={() => void handleDownloadAll(fund)}
                >
                  Download all
                </Button>
              ) : null}
              <Tooltip
                title={
                  fund.prog__kind === 'fund_group'
                    ? 'The books of each legal entity as of the period end, one workbook each, zipped: the statements, the schedule of investments, the partners capital, the trial balance and the journal lines, linked by formula'
                    : 'The books as of the period end as one workbook for the reviewer: the statements, the schedule of investments, the partners capital, the trial balance and the journal lines, linked by formula'
                }
              >
                <Button
                  size='small'
                  icon={<FileExcelOutlined />}
                  loading={reviewingFund === id}
                  disabled={reviewingFund !== null && reviewingFund !== id}
                  onClick={() => void handleReviewWorkbooks(fund)}
                >
                  Review workbook
                </Button>
              </Tooltip>
              <Popconfirm
                title={`Remove ${fund.prog__name} from the run?`}
                description={
                  stored
                    ? 'Its stored statements are moved to the trash. The fund can then join this or another run for the period.'
                    : 'The fund can then join this or another run for the period.'
                }
                okText='Remove'
                okButtonProps={{ danger: true }}
                disabled={isBusy(fund) || removeLocked}
                onConfirm={() => handleRemove(fund)}
              >
                <Tooltip
                  title={
                    isBusy(fund)
                      ? 'Its statements are being generated.'
                      : removeLocked
                        ? 'Its statements were issued: only an application administrator can remove it from the run.'
                        : undefined
                  }
                >
                  <span>
                    <Button danger size='small' loading={busy && !generating} disabled={isBusy(fund) || removeLocked}>
                      Remove
                    </Button>
                  </span>
                </Tooltip>
              </Popconfirm>
            </Space>
          )
        }
      }
    ],
    [
      busyFund,
      generating,
      openEntity,
      runGenerate,
      confirmGenerate,
      confirmIssue,
      handleRemove,
      handleIssue,
      handleDownloadAll,
      changes,
      isAdmin,
      issuingFund,
      downloadingFund,
      reviewingFund,
      handleReviewWorkbooks
    ]
  )

  // the fund being generated now, and the ones queued behind it
  const current = funds.find((f) => f.prog__state === RunFundState.generating)
  const queued = funds.filter((f) => f.prog__state === RunFundState.queued).length
  const currentTotal = current?.prog__counts.total ?? 0
  const currentDone = currentTotal - (current?.prog__counts.pending ?? 0)

  const generateAll = runHasGroups ? (
    // some fund produces its fund report too: Generate all per kind of document
    <Dropdown.Button
      size='small'
      loading={generating && busyFund === null}
      disabled={funds.length === 0 || generating || anyBusy}
      icon={<DownOutlined />}
      onClick={() => confirmGenerate(null, null, generatedBefore, run.run.prog__has_issued)}
      menu={{
        items: documentGroups.map((group) => ({
          key: group.statement_document_groups__code,
          label: `Generate all ${group.statement_document_groups__label.toLowerCase()}`,
          onClick: () =>
            confirmGenerate(
              null,
              groupChoice(group),
              funds.some((f) =>
                f.prog__groups.some(
                  (g) => g.statement_document_groups__code === group.statement_document_groups__code && g.prog__counts.total > 0
                )
              ),
              run.run.prog__has_issued
            )
        }))
      }}
    >
      {generatedBefore ? 'Generate all again' : 'Generate all'}
    </Dropdown.Button>
  ) : (
    <Button
      size='small'
      loading={generating && busyFund === null}
      disabled={funds.length === 0 || generating || anyBusy}
      onClick={generatedBefore ? undefined : () => void runGenerate(null)}
    >
      {generatedBefore ? 'Generate all again' : 'Generate all'}
    </Button>
  )

  const headerActions = (
    <>
      <Button size='small' icon={<PlusOutlined />} onClick={() => void openAdd()}>
        Add funds
      </Button>
      {funds.length > 0 ? (
        <Tooltip title='One zip with the Review workbook of every legal fund of the run, as of the period end - the set for the reviewer'>
          <Button
            size='small'
            icon={<FileExcelOutlined />}
            loading={reviewingFund === ''}
            disabled={reviewingFund !== null && reviewingFund !== ''}
            onClick={() => void handleReviewWorkbooks(null)}
          >
            Review workbooks
          </Button>
        </Tooltip>
      ) : null}
      {funds.length === 0 ? null : runHasGroups ? (
        generateAll
      ) : generatedBefore ? (
        <Popconfirm
          title='Generate every statement of the run again?'
          description={
            <div className='max-w-sm'>
              {REPLACE_NOTE}
              {run.run.prog__has_issued ? (
                <div className='mt-1'>
                  A statement that was issued is replaced by a new version, printed &quot;Revised&quot;.
                </div>
              ) : null}
            </div>
          }
          okText='Generate all again'
          disabled={generating || anyBusy}
          onConfirm={() => runGenerate(null)}
        >
          {generateAll}
        </Popconfirm>
      ) : (
        generateAll
      )}
    </>
  )

  const optionColumns: ColumnsType<InvestorStatementRunFundOption> = [
    { title: 'Fund', key: 'label', render: (_: unknown, o) => o.label },
    { title: 'Type', key: 'kind', width: 150, render: (_: unknown, o) => kindLabel(o.prog__kind) },
    {
      title: '',
      key: 'note',
      render: (_: unknown, o) =>
        o.prog__disabled_reason ? (
          <Text type='secondary'>{o.prog__disabled_reason}</Text>
        ) : (
          // what the fund's statements still need: it may join, and is
          // refused when generated until these are filled in
          o.prog__problems.map((p, i) => (
            <Tooltip key={`${p.code}-${p.funds__id ?? i}`} title={p.message}>
              <Tag color='gold'>{checkLabel(p.code)}</Tag>
            </Tooltip>
          ))
        )
    }
  ]

  return (
    <>
      {modalContext}
      <FormSection title='Funds' titleColor='purple' scrollable={false} headerActions={headerActions}>
        {current || queued > 0 ? (
          <div className='mb-2 flex items-center gap-3'>
            <Progress
              size='small'
              className='!mb-0 max-w-xs'
              percent={currentTotal > 0 ? Math.round((currentDone / currentTotal) * 100) : 0}
              showInfo={false}
              status='active'
            />
            <Text type='secondary'>
              {current
                ? currentTotal > 0
                  ? `Generating ${currentDone} of ${currentTotal} — ${current.prog__name}`
                  : `Reading the books of ${current.prog__name}`
                : 'Queued'}
              {queued > 0 ? ` · ${queued} fund${queued === 1 ? '' : 's'} waiting` : ''}
              {decksBusy ? ` · ${decks.pending} ${deckType?.investor_statement_types__short_label.toLowerCase() ?? 'performance review'}${decks.pending === 1 ? '' : 's'} to follow` : ''}
            </Text>
          </div>
        ) : decksBusy ? (
          // the funds are done; the performance decks of the run print after them
          <div className='mb-2 flex items-center gap-3'>
            <Progress
              size='small'
              className='!mb-0 max-w-xs'
              percent={decks.total > 0 ? Math.round(((decks.total - decks.pending) / decks.total) * 100) : 0}
              showInfo={false}
              status='active'
            />
            <Text type='secondary'>
              Generating {deckType?.investor_statement_types__short_label.toLowerCase() ?? 'performance review'}s{' '}
              {decks.total - decks.pending} of {decks.total}
            </Text>
          </div>
        ) : null}
        {funds.length === 0 ? (
          <Text type='secondary'>
            No fund is in the run yet. Add the funds whose statements for {run.run.prog__period_label} are to be made.
          </Text>
        ) : (
          <Table<InvestorStatementRunFund>
            size='small'
            pagination={false}
            rowKey='investor_statement_run_funds__id'
            columns={columns}
            dataSource={funds}
            scroll={RUN_TABLE_SCROLL}
            tableLayout='auto'
          />
        )}
      </FormSection>
      <Modal
        title='Add funds'
        open={add !== null}
        onCancel={() => setAdd(null)}
        onOk={handleAdd}
        okText='Add'
        confirmLoading={adding}
        okButtonProps={{ disabled: !add?.options || add.selected.length === 0 }}
        width={820}
        destroyOnHidden
      >
        {!add?.options ? (
          add?.error ? (
            <Alert type='error' showIcon message='Failed to read the funds that can join the run' description={add.error} />
          ) : (
            <div className='flex justify-center py-6'>
              <Spin />
            </div>
          )
        ) : (
          <>
            <div className='mb-3'>
              The funds chosen join the run for {run.run.prog__period_label}. Nothing is generated yet: use Generate
              once a fund&apos;s books for the period are complete.
            </div>
            <Table<InvestorStatementRunFundOption>
              size='small'
              pagination={false}
              rowKey='value'
              columns={optionColumns}
              dataSource={add.options}
              scroll={{ y: 380 }}
              rowSelection={{
                selectedRowKeys: add.selected,
                onChange: (keys) => setAdd((s) => (s ? { ...s, selected: keys.map(String) } : s)),
                getCheckboxProps: (o) => ({
                  disabled: !!o.prog__disabled_reason,
                  'aria-label': `Add ${o.label}`
                })
              }}
            />
          </>
        )}
      </Modal>
    </>
  )
}
